// Redeclare forwardRef
declare module "react" {
  function forwardRef<T, P = object>(
    render: (props: P, ref: React.Ref<T>) => React.ReactElement | null,
  ): (props: P & React.RefAttributes<T>) => React.ReactElement | null;
}

import React, { useCallback, useRef, useState, forwardRef, ForwardedRef, useEffect } from "react";
import { Input } from "./Input";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import styled from "styled-components";
import {
  ComboBoxOptionDefault,
  ComboBoxOptionSubtle,
  DropdownMenuContentDefault,
  DropdownMenuContentSubtle,
} from "./ComboBoxStyles";
import { debounce } from "@shared/common/utils/timing";

interface FormattedValueConverters<T> {
  format: (value?: T) => string;
  fromFormatted: (value?: string) => T | undefined;
  equals: (a: T, b: T) => boolean;
}
interface BackedValueConverters<T> extends FormattedValueConverters<T> {
  unserialize: (value?: string) => T | undefined;
  serialize: (value?: T) => string;
  equals: (a: T, b: T) => boolean;
}
type Converters<T> = FormattedValueConverters<T> | BackedValueConverters<T>;

function ensureBackedValueConverters<T>(converters: Converters<T>): BackedValueConverters<T> {
  if ("unserialize" in converters) {
    return converters;
  } else {
    return {
      ...converters,
      unserialize: converters.fromFormatted,
      serialize: converters.format,
    };
  }
}

interface ComboBoxProps<T> {
  readonly "data-testid"?: string;
  readonly value?: T;
  readonly defaultValue?: T;
  readonly onValueChange?: (value?: T) => void;
  readonly onBlur?: (value?: T) => void;
  readonly placeholder?: string;
  readonly label?: string;
  readonly width?: number | string;
  readonly iconButtonWidth?: number;
  readonly height?: number | string;

  readonly noBorder?: boolean;
  readonly Icon?: React.JSX.Element;
  readonly IconOpen?: React.JSX.Element;
  readonly itemStyle?: "default" | "subtle";
  readonly items: T[];
  readonly converters?: Converters<T>;
  readonly toNearestSelectableValue: (s?: T) => T | undefined;
  readonly style?: React.CSSProperties;
  readonly padding?: string | number;
}

const ScrollAreaRoot = styled(ScrollArea.Root)<{
  $height?: number | string;
  $width?: number | string;
}>`
  ${({ $width }) => ($width ? `width: ${$width}px` : "width: 100%")};
  ${({ $height }) => ($height ? `height: ${$height}px` : "height: 310px")};
  border-radius: 4px;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.background};
  --scrollbar-size: 8px;
`;

const ScrollAreaViewport = styled(ScrollArea.Viewport)`
  width: 100%;
  height: 100%;
  border-radius: inherit;
`;

const ScrollAreaScrollbar = styled(ScrollArea.Scrollbar)`
  display: flex;
  user-select: none;
  touch-action: none;
  padding: 2px;
  background: transparent;
  top: 20px !important;

  &:hover {
    background: transparent;
  }

  &[data-orientation="vertical"] {
    width: var(--scrollbar-size);
  }

  &[data-orientation="horizontal"] {
    flex-direction: column;
    height: var(--scrollbar-size);
  }
`;

const ScrollAreaThumb = styled(ScrollArea.Thumb)`
  flex: 1;
  background: ${({ theme }) => theme.colors.backgroundOverlay};
  border-radius: var(--scrollbar-size);
  position: relative;

  &:before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    height: 100%;
    min-width: 44px;
    min-height: 44px;
  }
`;

const ScrollAreaCorner = styled(ScrollArea.Corner)`
  background-color: transparent;
`;

function ComboBoxInner<T>(
  {
    "data-testid": comboBoxTestId,
    placeholder = "",
    onValueChange = () => {},
    onBlur = () => {},
    value,
    defaultValue,
    width = 96,
    iconButtonWidth = 40,
    items,
    toNearestSelectableValue,
    Icon = <tractie-icon name="chevron-up" />,
    IconOpen = <tractie-icon name="chevron-down" />,
    noBorder = false,
    converters: maybeBackedValueConverters = {
      format: (value) => (value ? value.toString() : ""),
      fromFormatted: (s) => s as T,
      equals: (a, b) => a === b,
    },
    style,
    padding = "0 0 0 8px",
    itemStyle = "default",
  }: ComboBoxProps<T>,
  ref: ForwardedRef<HTMLInputElement>,
) {
  const converters = ensureBackedValueConverters(maybeBackedValueConverters);
  const [isOpen, setIsOpen] = useState(false);
  // the user value is stored in a separate state to give the user the ability to type in the input field
  const [userValue, setUserValue] = useState<string>(converters.format(value));

  const DropdownMenuContent =
    itemStyle === "default" ? DropdownMenuContentDefault : DropdownMenuContentSubtle;

  // oxlint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOnValueChanged = useCallback(
    debounce(700, (value: string) => {
      onValueChange(converters.fromFormatted(value));
    }),
    [converters, onValueChange],
  );
  // handle textbox change event
  const updateOutput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setUserValue(e.target.value);
      debouncedOnValueChanged(e.target.value);
    },
    [debouncedOnValueChanged],
  );

  useEffect(() => {
    setUserValue(converters.format(value));
  }, [converters, value]);

  const scrollIntoView = useCallback(() => {
    if (!value) return;

    const scrollElement = scrollRef.current;

    if (!scrollElement) {
      return;
    }

    const valueOrNearestValue = toNearestSelectableValue(value);
    if (valueOrNearestValue === undefined) return;
    const index = Array.from(items.values()).reduce<number>((acc, prev, index) => {
      if (acc !== -1) return acc;
      if (converters.equals(prev, valueOrNearestValue)) return index;
      return -1;
    }, -1);

    const target = scrollElement.children[0].children[0].children[index] as HTMLElement;

    if (!target) return;
    target.scrollIntoView({
      block: "center",
      inline: "center",
      behavior: "instant",
    });
    target.focus();
  }, [converters, items, toNearestSelectableValue, value]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    setTimeout(scrollIntoView, 10);
  };

  const handleSelectItem = (newValue: T) => {
    onValueChange(newValue);
  };

  const scrollRef = useRef<HTMLDivElement>(null);

  return (
    <DropdownMenu.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Container $width={width} $noBorder={noBorder}>
        <Input
          data-testid={comboBoxTestId ? `${comboBoxTestId}-input` : undefined}
          autoComplete="off"
          data-state={isOpen ? "open" : "closed"}
          error={false}
          maxLength={5}
          placeholder={placeholder}
          ref={ref}
          style={{
            cursor: "default",
            ...style,
            padding,
            background: "none",
          }}
          width={typeof width === "number" ? width - iconButtonWidth : width}
          type="text"
          {...(userValue
            ? { value: userValue }
            : defaultValue
              ? { defaultValue: converters.format(defaultValue) }
              : {})}
          onChange={updateOutput}
          onBlur={(e) => onBlur(converters.fromFormatted(e.target.value))}
          noBorder={true}
        />
        <Trigger data-testid={comboBoxTestId ? `${comboBoxTestId}-trigger` : undefined}>
          {isOpen ? Icon : IconOpen}
        </Trigger>
      </Container>

      <DropdownMenuContent align="end" sideOffset={2} asChild>
        <ScrollAreaRoot $width={width} type="hover">
          <ScrollAreaViewport ref={scrollRef}>
            <DropdownMenu.RadioGroup
              value={converters.serialize(value)}
              onValueChange={(newValueAsString) => {
                const newValue = converters.unserialize(newValueAsString);
                if (newValue) handleSelectItem(newValue);
              }}
            >
              <DropdownItems
                items={items}
                converters={converters}
                itemStyle={itemStyle}
                width={width}
              />
            </DropdownMenu.RadioGroup>
          </ScrollAreaViewport>
          <ScrollAreaScrollbar orientation="vertical">
            <ScrollAreaThumb />
          </ScrollAreaScrollbar>
          <ScrollAreaCorner />
        </ScrollAreaRoot>
      </DropdownMenuContent>
    </DropdownMenu.Root>
  );
}

interface DropdownItemsProps<T> {
  readonly items: T[];
  readonly converters: BackedValueConverters<T>;
  readonly itemStyle: ComboBoxProps<T>["itemStyle"];
  readonly width: string | number;
}
function DropdownItems<T>({
  items,
  converters,
  itemStyle,
  width,
}: DropdownItemsProps<T>): React.JSX.Element {
  const ComboBoxOption = itemStyle === "default" ? ComboBoxOptionDefault : ComboBoxOptionSubtle;
  return (
    <>
      {items.map((item) => {
        const serialized = converters.serialize(item);
        return (
          <ComboBoxOption key={serialized} value={serialized} $width={width}>
            <div>{converters.format(item)}</div>
          </ComboBoxOption>
        );
      })}
    </>
  );
}

const Trigger = styled(DropdownMenu.Trigger)`
  border: none;
  background: none;
  outline: none;
  padding: 0;
  color: var(--tractie-color-content-emphasis);
`;

const Container = styled.div<{
  $width?: number | string;
  $height?: number | string;
  $noBorder: boolean;
}>`
  border-radius: 6px;
  border: ${({ $noBorder, theme }) =>
    $noBorder ? "none" : `1px solid ${theme.colors.borderInput}`};
  height: 40px;
  display: flex;
  flex-direction: row;
  gap: 0px;
  justify-content: space-between;
  ${({ $width }) => $width && typeof $width === "number" && `width: ${$width}px`};
  ${({ $height }) => $height && typeof $height === "number" && `width: ${$height}px`};

  &:focus-within {
    border: 1px solid ${({ theme, $noBorder }) => ($noBorder ? "none" : theme.colors.borderFocus)};
    outline: 1px solid ${({ theme, $noBorder }) => ($noBorder ? "none" : theme.colors.borderFocus)};
  }

  &:has(:hover) {
    background-color: ${({ theme }) => theme.colors.backgroundInput};
    border: 1px solid
      ${({ theme, $noBorder }) => ($noBorder ? "none" : theme.colors.borderInputHover)};
  }

  &:has([data-state="open"]) {
    border: 1px solid ${({ theme, $noBorder }) => ($noBorder ? "none" : theme.colors.borderFocus)};
    outline: 1px solid ${({ theme, $noBorder }) => ($noBorder ? "none" : theme.colors.borderFocus)};
  }
`;

export const ComboBox = forwardRef(ComboBoxInner);
